import React from 'react';
import { Edit3, Trash2, Shield, CheckCircle2, User, KeyRound } from 'lucide-react';
import { UserPermissionProfile } from '../../services/userManagementService';
import { ProtectedField } from '../common/ProtectedField';

interface Props {
  usuarios: UserPermissionProfile[];
  loading: boolean;
  onEditUser: (user: UserPermissionProfile) => void;
  onEditPermissions: (user: UserPermissionProfile) => void;
  onToggleStatus: (user: UserPermissionProfile) => void;
  onDeleteUser: (user: UserPermissionProfile) => void;
  onRestoreUser?: (user: UserPermissionProfile) => void;
}

export const UsuariosTable: React.FC<Props> = ({
  usuarios,
  loading,
  onEditUser,
  onEditPermissions,
  onToggleStatus,
  onDeleteUser,
  onRestoreUser
}) => {
  return (
    <div className="bg-white shadow-xs rounded-xl overflow-hidden border border-slate-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-xs">
          <thead className="bg-slate-50 font-bold text-slate-600 uppercase tracking-wider">
            <tr>
              <th className="px-5 py-3.5 text-left">ID</th>
              <th className="px-5 py-3.5 text-left">Usuário & Perfil</th>
              <th className="px-5 py-3.5 text-left">E-mail / Login</th>
              <th className="px-5 py-3.5 text-left">Supabase Auth (auth.users)</th>
              <th className="px-5 py-3.5 text-left">Status</th>
              <th className="px-5 py-3.5 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500 font-medium">
                  Carregando usuários do sistema...
                </td>
              </tr>
            ) : usuarios.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500 font-medium">
                  Nenhum usuário encontrado para os filtros aplicados.
                </td>
              </tr>
            ) : (
              usuarios.map((u) => {
                const isActive = u.active === 'Y' || u.active === 'S';

                return (
                  <tr key={u.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-5 py-4 font-bold text-slate-900">#{u.id}</td>

                    {/* Nome & Perfil */}
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-xs uppercase border border-slate-200 shrink-0">
                          {u.name ? u.name.substring(0, 2) : 'US'}
                        </div>
                        <div>
                          <div className="text-slate-900 font-bold text-sm leading-tight">
                            {u.name}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {u.groups.length === 0 ? (
                              <span className="text-[10px] text-slate-400 italic">
                                Sem grupo atribuído
                              </span>
                            ) : (
                              u.groups.map((g) => (
                                <span
                                  key={g.id}
                                  className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200"
                                >
                                  {g.name}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* E-mail / Login */}
                    <td className="px-5 py-4">
                      <div className="font-bold text-slate-800">{u.email || u.login}</div>
                      <div className="text-slate-500 text-[11px]">@{u.login}</div>
                    </td>

                    {/* Supabase Auth UUID */}
                    <td className="px-5 py-4 font-mono text-[11px]">
                      {u.auth_user_id ? (
                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-lg border border-emerald-200 font-bold">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                          {u.auth_user_id.substring(0, 8)}...{u.auth_user_id.substring(u.auth_user_id.length - 4)}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200">
                          Não vinculado
                        </span>
                      )}
                    </td>

                    {/* Status Toggle */}
                    <td className="px-5 py-4">
                      <ProtectedField
                        permission="edit_user"
                        fallback={
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${
                              u.deleted 
                                ? 'bg-rose-100 text-rose-800 border border-rose-200' 
                                : isActive
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            <span
                              className={`w-2 h-2 rounded-full ${u.deleted ? 'bg-rose-500' : isActive ? 'bg-emerald-500' : 'bg-slate-400'}`}
                            />
                            {u.deleted ? 'Excluído' : isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        }
                      >
                        {u.deleted ? (
                          <button
                            onClick={() => onRestoreUser && onRestoreUser(u)}
                            title="Clique para restaurar o usuário excluído"
                            className="px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1.5 cursor-pointer bg-rose-100 text-rose-800 border border-rose-200 hover:bg-rose-200"
                          >
                            <span className="w-2 h-2 rounded-full bg-rose-500" />
                            Excluído (Restaurar)
                          </button>
                        ) : (
                          <button
                            onClick={() => onToggleStatus(u)}
                            title="Clique para alternar o status do usuário"
                            className={`px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                              isActive
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200'
                                : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                            }`}
                          >
                            <span
                              className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`}
                            />
                            {isActive ? 'Ativo' : 'Inativo'}
                          </button>
                        )}
                      </ProtectedField>
                    </td>

                    {/* Ações */}
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Botão Editar Usuário */}
                        <ProtectedField permission="edit_user">
                          <button
                            onClick={() => onEditUser(u)}
                            className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-bold flex items-center gap-1.5 border border-indigo-200 transition shadow-2xs cursor-pointer"
                            title="Editar dados, e-mail, senha e perfil do usuário"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            Editar
                          </button>
                        </ProtectedField>

                        {/* Botão Permissões RBAC */}
                        <ProtectedField permission="manage_permissions">
                          <button
                            onClick={() => onEditPermissions(u)}
                            className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-bold flex items-center gap-1 border border-slate-200 transition cursor-pointer"
                            title="Gerenciar matriz de permissões RBAC"
                          >
                            <Shield className="w-3.5 h-3.5 text-slate-500" />
                            Permissões
                          </button>
                        </ProtectedField>

                        {/* Botão Excluir */}
                        <ProtectedField permission="delete_user">
                          <button
                            onClick={() => onDeleteUser(u)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition border border-transparent hover:border-rose-200 cursor-pointer"
                            title="Excluir usuário"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </ProtectedField>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
